import React, { useEffect, useState } from 'react';
import { Loader2, ChevronRight, FileText, Bell, ArrowRight, Phone, Mail, CalendarCheck, PartyPopper, Sparkles, Check } from 'lucide-react';
import { clientApi, formatCurrency } from '../../components/client-portal/clientPortalApi';

// Premium micro-animations (injected once) — brief §57 "expensive, not gimmicky".
if (typeof document !== 'undefined' && !document.getElementById('cp-dash-css')) {
  const s = document.createElement('style');
  s.id = 'cp-dash-css';
  s.textContent = `
@keyframes cpUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
.cp-up{animation:cpUp .5s cubic-bezier(.22,.61,.36,1) both}
.cp-bar>span{transition:width 1.1s cubic-bezier(.22,.61,.36,1)}
@media (prefers-reduced-motion:reduce){.cp-up{animation:none}.cp-bar>span{transition:none}}`;
  document.head.appendChild(s);
}

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

const HERO = {
  application: { bg: 'from-[#0F1A13] via-[#12241a] to-[#173a29]', accent: '#A9FF53', tag: 'Your home loan' },
  approved: { bg: 'from-[#0b2a1e] via-[#0f3a29] to-[#12603f]', accent: '#7CF5B0', tag: 'Your home loan' },
  settled: { bg: 'from-[#10331f] via-[#125a35] to-[#0ea05a]', accent: '#EAFBEF', tag: 'Settled' },
  post_settlement: { bg: 'from-[#0F1A13] via-[#12241a] to-[#173a29]', accent: '#A9FF53', tag: 'Your home loan' },
};

export default function ClientDashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const d = await clientApi('/client/dashboard');
        if (!cancel) setData(d);
      } catch (e) { if (!cancel) setErr(e.message); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, []);

  if (loading) {
    return (
      <div className="space-y-5" data-testid="client-dashboard-loading">
        <div className="h-8 w-64 rounded-lg bg-slate-100 animate-pulse" />
        <div className="h-56 rounded-3xl bg-slate-100 animate-pulse" />
        <div className="grid sm:grid-cols-2 gap-4"><div className="h-28 rounded-2xl bg-slate-100 animate-pulse" /><div className="h-28 rounded-2xl bg-slate-100 animate-pulse" /></div>
      </div>
    );
  }
  if (err) return <div className="rounded-2xl bg-rose-50 text-rose-700 ring-1 ring-rose-200 p-5 text-sm" data-testid="client-dashboard-error">{err}</div>;
  if (!data) return null;

  const jb = data.journey_block || {};
  const hero = HERO[jb.mode] || HERO.application;
  const broker = data.broker || {};
  const initials = (broker.name || 'D F').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="client-dashboard">
      {/* Greeting */}
      <div className="cp-up">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900" data-testid="dash-greeting">
          {greeting()}, {data.first_name}
        </h1>
        <p className="text-slate-500 mt-1 text-sm">Here's where things are up to with {broker.business || 'your broker'}.</p>
      </div>

      {/* Action Required — always first when present (§77) */}
      {data.urgent_task && (
        <button
          onClick={() => onNavigate?.('documents')}
          data-testid="dash-urgent-task"
          className="cp-up w-full text-left rounded-2xl bg-amber-50 ring-1 ring-amber-200 hover:ring-amber-300 transition p-5 flex items-center gap-4"
        >
          <div className="w-11 h-11 rounded-xl bg-amber-500/15 flex items-center justify-center flex-none">
            <FileText className="w-5 h-5 text-amber-700" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Action required</div>
            <div className="font-semibold text-slate-900 truncate">{data.urgent_task.title}</div>
            {data.urgent_task.reason && <div className="text-sm text-amber-800/80 truncate">{data.urgent_task.reason}</div>}
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-amber-800 flex-none">Upload now <ArrowRight className="w-4 h-4" /></span>
        </button>
      )}

      {/* Dynamic hero card */}
      <div className={`cp-up cp-dark rounded-3xl bg-gradient-to-br ${hero.bg} text-white p-7 sm:p-9 shadow-2xl relative overflow-hidden`} data-testid="dash-hero" data-mode={jb.mode}>
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full opacity-[0.08]" style={{ background: hero.accent }} />
        <div className="relative">
          <div className="flex items-center gap-2 text-white/70 text-xs uppercase tracking-widest font-medium">
            {(jb.mode === 'settled' || jb.celebrate) && <PartyPopper className="w-4 h-4" style={{ color: hero.accent }} />}
            {hero.tag}
          </div>

          {(jb.mode === 'settled') ? (
            <>
              <h2 className="text-3xl sm:text-4xl font-bold mt-2 text-white" data-testid="dash-hero-title">Congratulations, {data.first_name} 🎉</h2>
              <p className="text-white/80 mt-2 max-w-lg">{jb.whats_happening}</p>
              <button onClick={() => onNavigate?.('after_settlement')} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold rounded-full px-5 py-2.5 text-[#0F1A13]" style={{ background: hero.accent }} data-testid="dash-hero-cta">
                View my home loan <ArrowRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <h2 className="text-2xl sm:text-3xl font-bold mt-2 text-white" data-testid="dash-hero-title">{jb.title}</h2>
              <p className="text-white/80 mt-2 max-w-lg text-[15px] leading-relaxed">
                {jb.whats_happening}{jb.lender && jb.mode === 'application' && jb.phase_index >= 2 ? ` (${jb.lender})` : ''}
              </p>

              {/* 6-step progress */}
              <div className="mt-7">
                <div className="cp-bar h-1.5 rounded-full bg-white/15 overflow-hidden">
                  <span className="block h-full rounded-full" style={{ width: `${jb.progress_percent}%`, background: hero.accent }} />
                </div>
                <div className="mt-4 grid grid-cols-6 gap-1">
                  {(jb.steps || []).map((s) => (
                    <div key={s.label} className="flex flex-col items-center text-center">
                      <span className="w-5 h-5 rounded-full flex items-center justify-center mb-1.5"
                        style={{
                          background: s.state === 'done' ? hero.accent : s.state === 'current' ? 'transparent' : 'transparent',
                          border: s.state === 'upcoming' ? '1.5px solid rgba(255,255,255,.25)' : `1.5px solid ${hero.accent}`,
                        }}>
                        {s.state === 'done' ? <Check className="w-3 h-3 text-[#0F1A13]" strokeWidth={3} /> : s.state === 'current' ? <span className="w-1.5 h-1.5 rounded-full" style={{ background: hero.accent }} /> : null}
                      </span>
                      <span className={`text-[10px] sm:text-[11px] leading-tight ${s.state === 'upcoming' ? 'text-white/40' : 'text-white/90 font-medium'}`}>{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {jb.next_hint && (
                <div className="mt-6 rounded-2xl bg-white/10 ring-1 ring-white/15 px-4 py-3 flex items-start gap-2.5">
                  <Sparkles className="w-4 h-4 mt-0.5 flex-none" style={{ color: hero.accent }} />
                  <div><div className="text-[11px] uppercase tracking-wider text-white/60 font-semibold">What's next</div><div className="text-sm text-white/90">{jb.next_hint}</div></div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Quick tiles */}
      <div className="grid grid-cols-2 gap-4 cp-up">
        <button onClick={() => onNavigate?.('documents')} data-testid="dash-tile-documents" className="text-left rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-emerald-300 hover:shadow-md transition p-5">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center mb-3"><FileText className="w-5 h-5 text-amber-600" strokeWidth={1.75} /></div>
          <div className="text-3xl font-semibold text-slate-900 tabular-nums">{data.outstanding_documents}</div>
          <div className="text-sm text-slate-500">Documents needed</div>
        </button>
        <button onClick={() => onNavigate?.('notifications')} data-testid="dash-tile-updates" className="text-left rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-emerald-300 hover:shadow-md transition p-5">
          <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center mb-3"><Bell className="w-5 h-5 text-sky-600" strokeWidth={1.75} /></div>
          <div className="text-3xl font-semibold text-slate-900 tabular-nums">{data.unread_notifications}</div>
          <div className="text-sm text-slate-500">New updates</div>
        </button>
      </div>

      {/* Broker card (§46) */}
      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 flex items-center gap-4 cp-up" data-testid="dash-broker">
        {broker.photo
          ? <img src={broker.photo} alt={broker.name} className="w-14 h-14 rounded-2xl object-cover flex-none" />
          : <div className="w-14 h-14 rounded-2xl bg-[#0F1A13] text-white flex items-center justify-center font-semibold text-lg flex-none">{initials}</div>}
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Your broker</div>
          <div className="font-semibold text-slate-900 truncate">{broker.name}</div>
          <div className="text-sm text-slate-500 truncate">{broker.role}</div>
        </div>
        <div className="flex gap-2 flex-none">
          {broker.phone && <a href={`tel:${broker.phone}`} className="w-10 h-10 rounded-xl ring-1 ring-slate-200 hover:bg-slate-50 flex items-center justify-center" title="Call" data-testid="broker-call"><Phone className="w-4 h-4 text-slate-600" /></a>}
          {broker.email && <a href={`mailto:${broker.email}`} className="w-10 h-10 rounded-xl ring-1 ring-slate-200 hover:bg-slate-50 flex items-center justify-center" title="Email" data-testid="broker-email"><Mail className="w-4 h-4 text-slate-600" /></a>}
          <button onClick={() => onNavigate?.('messages')} className="w-10 h-10 rounded-xl ring-1 ring-slate-200 hover:bg-slate-50 flex items-center justify-center" title="Book / message" data-testid="broker-book"><CalendarCheck className="w-4 h-4 text-slate-600" /></button>
        </div>
      </div>

      {/* Loan summary (kept concise) */}
      {(data.loan?.purpose || data.loan?.loan_amount || data.loan?.property_address) && (
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6 cp-up" data-testid="dash-loan-summary">
          <h3 className="text-base font-semibold text-slate-900 mb-4">Your application</h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <Item label="Purpose" value={data.loan?.purpose} />
            <Item label="Property" value={data.loan?.property_address} />
            <Item label="Loan amount" value={data.loan?.loan_amount ? formatCurrency(data.loan.loan_amount) : null} />
            <Item label="Current stage" value={jb.title} />
          </dl>
        </div>
      )}

      {/* Recent updates */}
      <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6 cp-up" data-testid="dash-updates">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-slate-900">Recent updates</h3>
          <button onClick={() => onNavigate?.('notifications')} className="text-sm font-medium text-emerald-700 inline-flex items-center gap-1">All updates <ChevronRight className="w-4 h-4" /></button>
        </div>
        {(!data.recent_updates || data.recent_updates.length === 0) ? (
          <div className="text-slate-500 text-sm py-2">You're all up to date — we'll post updates here as things progress.</div>
        ) : (
          <ul className="space-y-4">
            {data.recent_updates.map((n) => (
              <li key={n.id} className="flex items-start gap-3 text-sm">
                <div className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500 flex-none" />
                <div className="flex-1"><div className="font-medium text-slate-800">{n.title}</div><div className="text-slate-500">{n.body}</div></div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Item({ label, value }) {
  return (
    <div>
      <dt className="text-slate-400 text-[11px] uppercase tracking-wider font-semibold">{label}</dt>
      <dd className="text-slate-800 font-medium mt-1">{value || '—'}</dd>
    </div>
  );
}
